import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { DealerInfoFields } from "@/components/admin/DealerInfoFields";
import { useAdminAccess } from "@/lib/adminAccess";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getDealers, updateDealerStatus, getDealerDetail, updateDealerProfile,
  resetDealerPassword, getDealerLocations, assignDealerLocations,
  getDealerNotes, addDealerNote, getDealerBilling, updateDealerBilling,
  getDealerDocuments, uploadDealerDocument, deleteDealerDocument,
  getLocations, getDealerVidapay, updateDealerVidapay,
  getDealerEmployees, createDealerEmployee, updateDealerEmployee, deleteDealerEmployee,
  getDealerOwnerAccount, updateDealerOwnerAccount,
  adminCreateDealer, bulkUploadDealers,
  adminGetRequiredDocuments, adminGetPreApprovedDocuments, adminGetPreApprovedData,
  adminDeletePreApprovedDocument, reviewDealerDocument, reviewPreApprovedDocument,
} from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Users, Filter, Search, UserCheck, UserX, Mail, Phone, Eye,
  Edit, Loader2, MapPin, MessageSquare, CreditCard, FileText,
  Upload, Trash2, Key, Clock, Building, Download, Zap, UserPlus, Plus,
  CheckCircle2, XCircle, AlertCircle, Camera, Shield,
} from "lucide-react";
import { exportToCSV } from "@/lib/exportCSV";
import { ReassignStoresDialog } from "./dealers/ReassignStoresDialog";
import { BulkUploadDealersDialog } from "./dealers/BulkUploadDealersDialog";
import { EmployeeStoreAccess } from "@/components/shared/EmployeeStoreAccess";
import { CoOwnersSection } from "@/components/shared/CoOwnersSection";

const STATUSES = [["approved", "Active"], ["pending", "Pending"], ["inactive", "Inactive"], ["closed", "Closed"], ["suspended", "Suspended"]];
const STATUS_STYLE = { approved: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", inactive: "bg-slate-200 text-slate-700",
  closed: "bg-red-100 text-red-700", suspended: "bg-orange-100 text-orange-700" };
const StatusBadge = ({ status, testId }) => (
  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`} data-testid={testId}>
    {(STATUSES.find(([v]) => v === status) || [null, status || "Pending"])[1]}
  </span>
);


const ALL_DEALER_PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "dealer_sales", label: "Dealer Sales" },
  { key: "locations", label: "Locations" },
  { key: "knowledge_base", label: "Knowledge Base" },
  { key: "announcements", label: "Announcements" },
  { key: "submit_location", label: "Submit Location" },
  { key: "training", label: "Training" },
  { key: "documents", label: "Documents" },
  { key: "marketplace", label: "Marketplace" },
  { key: "visits", label: "Visits" },
  { key: "chat", label: "Chat" },
];
const REQUIRED_PERMS = ["knowledge_base", "announcements", "training", "visits"];

export default function AdminDealers() {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [detailTab, setDetailTab] = useState("profile");
  const [saving, setSaving] = useState(false);

  // Detail data
  const [profileData, setProfileData] = useState({});
  const [allLocations, setAllLocations] = useState([]);
  const [assignedLocationIds, setAssignedLocationIds] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [billing, setBilling] = useState({});
  const [documents, setDocuments] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState(null);
  const [preApprovedDocs, setPreApprovedDocs] = useState(null);
  const [preApprovedData, setPreApprovedData] = useState({});
  const { can } = useAdminAccess();
  const [reviewingDoc, setReviewingDoc] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [vidapay, setVidapay] = useState({});
  const [employees, setEmployees] = useState([]);
  const [empDialog, setEmpDialog] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "employee", permissions: [] });
  const [empSaving, setEmpSaving] = useState(false);
  const [ownerAccount, setOwnerAccount] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [ownerPerms, setOwnerPerms] = useState([]);
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createTab, setCreateTab] = useState("profile");
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [locSearch, setLocSearch] = useState("");
  const [createForm, setCreateForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company_name: "", password: "", address: "", city: "", state: "", zip_code: "" });

  const fetchDealers = async () => {
    try {
      const data = await getDealers(statusFilter === "all" ? null : statusFilter);
      setDealers(data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDealers(); }, [statusFilter]);

  const filteredDealers = dealers.filter((d) =>
    d.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.company_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => (a.company_name || "\uffff").localeCompare(b.company_name || "\uffff", undefined, { sensitivity: "base" })
    || `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));

  const handleOpenDetail = async (dealer) => {
    setSelectedDealer(dealer);
    setDetailTab("profile");
    setProfileData({ ...dealer });
    try {
      const [detail, locs, n, b, docs, vp] = await Promise.all([
        getDealerDetail(dealer.id),
        getLocations(false),
        getDealerNotes(dealer.id),
        getDealerBilling(dealer.id),
        getDealerDocuments(dealer.id),
        getDealerVidapay(dealer.id),
      ]);
      setProfileData({ ...detail });
      setAllLocations(locs);
      setAssignedLocationIds(detail.assigned_locations || []);
      setNotes(n);
      setBilling(b);
      setDocuments(docs);
      setVidapay(vp);
      try { const emps = await getDealerEmployees(dealer.id); setEmployees(emps); } catch { setEmployees([]); }
      try {
        const oa = await getDealerOwnerAccount(dealer.id);
        setOwnerAccount(oa);
        setOwnerEmail(oa.email || "");
        setOwnerPerms(oa.dealer_permissions || []);
      } catch { setOwnerAccount(null); }
      setOwnerPassword("");
      // Fetch document review data
      try {
        const [reqDocs, paDocs, paData] = await Promise.allSettled([
          adminGetRequiredDocuments(dealer.id),
          adminGetPreApprovedDocuments(dealer.id),
          adminGetPreApprovedData(dealer.id),
        ]).then((rs) => rs.map((r) => (r.status === "fulfilled" ? r.value : null)));
        setRequiredDocs(reqDocs);
        setPreApprovedDocs(paDocs);
        setPreApprovedData(paData || {});
      } catch { setRequiredDocs(null); setPreApprovedDocs(null); setPreApprovedData({}); }
    } catch (e) { console.error(e); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateDealerProfile(selectedDealer.id, profileData);
      toast.success("Profile updated");
      fetchDealers();
    } catch { toast.error("Failed to update profile"); }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword) { toast.error("Enter a new password"); return; }
    setSaving(true);
    try {
      await resetDealerPassword(selectedDealer.id, newPassword);
      toast.success("Password reset");
      setNewPassword("");
    } catch { toast.error("Failed to reset password"); }
    setSaving(false);
  };

  const handleToggleLocation = (locId) => {
    setAssignedLocationIds((prev) =>
      prev.includes(locId) ? prev.filter((id) => id !== locId) : [...prev, locId]
    );
  };

  const handleSaveLocations = async () => {
    setSaving(true);
    try {
      await assignDealerLocations(selectedDealer.id, assignedLocationIds);
      toast.success("Locations assigned");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail) {
        const msg = typeof detail === "string" ? detail : (detail.message || "This location is already assigned to another dealer.");
        toast.error(msg, { duration: 7000 });
      } else {
        toast.error("Failed to assign locations");
      }
    }
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const n = await addDealerNote(selectedDealer.id, newNote);
      setNotes([n, ...notes]);
      setNewNote("");
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
  };

  const handleSaveBilling = async () => {
    setSaving(true);
    try {
      await updateDealerBilling(selectedDealer.id, billing);
      toast.success("Billing info updated");
    } catch { toast.error("Failed to update billing"); }
    setSaving(false);
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    try {
      const doc = await uploadDealerDocument(selectedDealer.id, file);
      setDocuments([doc, ...documents]);
      toast.success("Document uploaded");
    } catch { toast.error("Failed to upload document"); }
    setSaving(false);
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await deleteDealerDocument(selectedDealer.id, docId);
      setDocuments(documents.filter((d) => d.id !== docId));
      toast.success("Document deleted");
      refreshDocData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeletePreApprovedDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await adminDeletePreApprovedDocument(selectedDealer.id, docId);
      toast.success("Document deleted");
      refreshDocData();
    } catch { toast.error("Failed to delete"); }
  };

  const refreshDocData = async () => {
    if (!selectedDealer) return;
    try {
      const [reqDocs, paDocs, docs] = await Promise.all([
        adminGetRequiredDocuments(selectedDealer.id),
        adminGetPreApprovedDocuments(selectedDealer.id),
        getDealerDocuments(selectedDealer.id),
      ]);
      setRequiredDocs(reqDocs);
      setPreApprovedDocs(paDocs);
      setDocuments(docs);
    } catch {}
  };

  const handleReviewDocument = async (docId, reviewStatus, collection = "required") => {
    try {
      if (collection === "required") {
        await reviewDealerDocument(selectedDealer.id, docId, { review_status: reviewStatus, review_notes: reviewNotes });
      } else {
        await reviewPreApprovedDocument(selectedDealer.id, docId, { review_status: reviewStatus, review_notes: reviewNotes });
      }
      toast.success(`Document ${reviewStatus}`);
      setReviewingDoc(null);
      setReviewNotes("");
      refreshDocData();
    } catch { toast.error("Failed to update review status"); }
  };

  const handleSaveVidapay = async () => {
    setSaving(true);
    try {
      await updateDealerVidapay(selectedDealer.id, vidapay);
      toast.success("VidaPay info updated");
    } catch { toast.error("Failed to update VidaPay"); }
    setSaving(false);
  };

  const openEmpDialog = (emp = null) => {
    setEditingEmp(emp);
    setEmpForm(emp ? { first_name: emp.first_name, last_name: emp.last_name, email: emp.email, password: "", role: emp.role || "employee", permissions: emp.permissions || ALL_DEALER_PERMISSIONS.map(p => p.key), allowed_location_ids: emp.allowed_location_ids ?? null }
      : { first_name: "", last_name: "", email: "", password: "", role: "employee", permissions: ALL_DEALER_PERMISSIONS.map(p => p.key), allowed_location_ids: null });
    setEmpDialog(true);
  };

  const handleSaveOwnerAccount = async () => {
    setOwnerSaving(true);
    try {
      const payload = {};
      if (ownerEmail && ownerEmail !== ownerAccount?.email) payload.email = ownerEmail;
      if (ownerPassword) payload.password = ownerPassword;
      payload.dealer_permissions = ownerPerms;
      await updateDealerOwnerAccount(selectedDealer.id, payload);
      toast.success("Owner account updated");
      setOwnerPassword("");
      const oa = await getDealerOwnerAccount(selectedDealer.id);
      setOwnerAccount(oa);
      setOwnerEmail(oa.email || "");
      setOwnerPerms(oa.dealer_permissions || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update owner account"); }
    setOwnerSaving(false);
  };

  const handleSaveEmployee = async () => {
    if (!empForm.first_name || !empForm.email) return toast.error("Name and email required");
    if (!editingEmp && !empForm.password) return toast.error("Password required for new employee");
    setEmpSaving(true);
    try {
      if (editingEmp) {
        const payload = { ...empForm };
        if (!payload.password) delete payload.password;
        await updateDealerEmployee(selectedDealer.id, editingEmp.id, payload);
        toast.success("Employee updated");
      } else {
        await createDealerEmployee(selectedDealer.id, empForm);
        toast.success("Employee created");
      }
      setEmpDialog(false);
      const emps = await getDealerEmployees(selectedDealer.id);
      setEmployees(emps);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save employee"); }
    setEmpSaving(false);
  };

  const handleDeleteEmployee = async (empId) => {
    if (!window.confirm("Delete this employee?")) return;
    try {
      await deleteDealerEmployee(selectedDealer.id, empId);
      setEmployees(employees.filter((e) => e.id !== empId));
      toast.success("Employee deleted");
    } catch { toast.error("Failed to delete employee"); }
  };

  const handleCreateDealer = async () => {
    if (!createForm.first_name || !createForm.email || !createForm.password) return toast.error("First name, email, and password are required");
    setSubmitting(true);
    try {
      const newDealer = await adminCreateDealer(createForm);
      toast.success("Dealer created! You can now manage all tabs.");
      setIsCreateOpen(false);
      setCreateTab("profile");
      setCreateForm({ first_name: "", last_name: "", email: "", phone: "", company_name: "", password: "", address: "", city: "", state: "", zip_code: "" });
      fetchDealers();
      // Auto-open the edit dialog with all tabs functional
      handleOpenDetail(newDealer);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create dealer"); }
    setSubmitting(false);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSubmitting(true);
    try {
      const result = await bulkUploadDealers(file);
      toast.success(`Created ${result.created} dealers${result.errors?.length ? ` (${result.errors.length} errors)` : ""}`);
      if (result.errors?.length) result.errors.forEach((err) => toast.error(err));
      fetchDealers();
      setIsBulkOpen(false);
    } catch { toast.error("Bulk upload failed"); }
    setSubmitting(false);
    e.target.value = "";
  };

  const downloadSampleCSV = () => {
    const content = "first_name,last_name,email,phone,company_name,address,city,state,zip_code,password\nJohn,Doe,john@example.com,555-0100,Acme Wireless,123 Main St,Miami,FL,33101,dealer123\nJane,Smith,jane@example.com,555-0200,Smith Mobile,456 Oak Ave,Orlando,FL,32801,dealer123";
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_dealers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const [reassignFor, setReassignFor] = useState(null); // { dealer, targetStatus, locations }
  const [reassignChoices, setReassignChoices] = useState({}); // location_id → "unassign" | "remove" | "<dealerId>"
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  const openReassignFlow = async (dealer, targetStatus) => {
    try {
      const locs = await getDealerLocations(dealer.id);
      if (!locs || locs.length === 0) {
        // No stores to reassign — just update the status
        await updateDealerStatus(dealer.id, targetStatus);
        toast.success(`Dealer marked ${targetStatus}`);
        fetchDealers();
        return;
      }
      // Default action: unassign for inactive; remove for closed
      const defaultAction = targetStatus === "closed" ? "remove" : "unassign";
      const initial = {};
      locs.forEach((l) => { initial[l.id] = defaultAction; });
      setReassignChoices(initial);
      setReassignFor({ dealer, targetStatus, locations: locs });
    } catch {
      toast.error("Failed to load dealer locations");
    }
  };

  const handleStatusChange = async (dealerId, newStatus) => {
    // For inactive / closed, open the reassignment flow first
    if (newStatus === "inactive" || newStatus === "closed") {
      const dealer = dealers.find((d) => d.id === dealerId);
      if (dealer) return openReassignFlow(dealer, newStatus);
    }
    try {
      await updateDealerStatus(dealerId, newStatus);
      toast.success(`Dealer status set to ${newStatus}`);
      fetchDealers();
    } catch { toast.error("Failed to update"); }
  };

  const submitReassignments = async () => {
    if (!reassignFor) return;
    const { dealer, targetStatus, locations } = reassignFor;
    const reassignments = locations.map((loc) => {
      const choice = reassignChoices[loc.id];
      if (!choice || choice === "unassign") return { location_id: loc.id, action: "unassign" };
      if (choice === "remove") return { location_id: loc.id, action: "remove" };
      return { location_id: loc.id, new_dealer_id: choice };
    });
    setReassignSubmitting(true);
    try {
      await updateDealerStatus(dealer.id, targetStatus, reassignments);
      toast.success(`Dealer marked ${targetStatus} — stores updated`);
      setReassignFor(null);
      setReassignChoices({});
      fetchDealers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update dealer");
    } finally {
      setReassignSubmitting(false);
    }
  };

  const ReviewBadge = ({ status }) => {
    if (status === "approved") return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full"><CheckCircle2 className="w-2.5 h-2.5" /> Approved</span>;
    if (status === "rejected") return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full"><XCircle className="w-2.5 h-2.5" /> Rejected</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full"><AlertCircle className="w-2.5 h-2.5" /> Pending</span>;
  };

  const DocSection = ({ title, icon, docs, onDelete, onReview }) => (
    <div>
      <h3 className="text-sm font-semibold text-navy mb-2 flex items-center gap-2">
        {icon} {title}
        <span className="text-xs font-normal text-slate-400">({docs.filter(r => r.status === "uploaded").length}/{docs.length})</span>
      </h3>
      <div className="space-y-2">
        {docs.map((req) => {
          const allDocs = req.documents || [];
          return (
            <div key={req.key} className={`p-3 rounded-lg border ${allDocs.length > 0 ? "border-slate-200 bg-white" : "border-dashed border-slate-300 bg-slate-50/50"}`} data-testid={`admin-pa-doc-${req.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className={`w-4 h-4 flex-shrink-0 ${allDocs.length > 0 ? "text-teal" : "text-slate-300"}`} />
                  <span className="text-sm font-medium text-navy">{req.label}</span>
                  {allDocs.length > 0 && <span className="text-xs text-slate-400">{allDocs.length} file(s)</span>}
                </div>
                {allDocs.length === 0 && <span className="text-xs text-slate-400 italic">Not uploaded</span>}
              </div>
              {allDocs.length > 0 && (
                <div className="mt-2 space-y-1.5 pl-6">
                  {allDocs.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded" data-testid={`admin-pa-file-${doc.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {doc.content_type?.startsWith("image/") ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        )}
                        <span className="truncate text-slate-600">{doc.original_filename}</span>
                        <ReviewBadge status={doc.review_status} />
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><Eye className="w-3 h-3" /></Button>
                        </a>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onReview(doc)}><Shield className="w-3 h-3 text-amber-500" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(doc.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <AdminLayout title="Dealer Management" breadcrumb="Dealers">
      <div className="space-y-6" data-testid="admin-dealers-page">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search dealers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64" data-testid="dealer-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="dealer-status-filter"><Filter className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dealers</SelectItem>
                <SelectItem value="approved">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-slate-500 font-body flex items-center gap-2"><Users className="w-4 h-4" />{filteredDealers.length} dealers</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportToCSV(filteredDealers, [
              { label: "First Name", accessor: "first_name" },
              { label: "Last Name", accessor: "last_name" },
              { label: "Email", accessor: "email" },
              { label: "Phone", accessor: "phone" },
              { label: "Company", accessor: "company_name" },
              { label: "City", accessor: "city" },
              { label: "State", accessor: "state" },
              { label: "Status", accessor: "status" },
              { label: "Date", accessor: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "" },
            ], "dealers")} data-testid="export-dealers-btn">
              <Download className="w-4 h-4 mr-2" />Export
            </Button>
            <Button variant="outline" onClick={() => setIsBulkOpen(true)} data-testid="bulk-upload-dealers-btn">
              <Upload className="w-4 h-4 mr-2" />Bulk Upload
            </Button>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-teal hover:bg-teal-dark text-white" data-testid="create-dealer-btn">
              <Plus className="w-4 h-4 mr-2" />Add Dealer
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-slate-500 text-center py-8 font-body">Loading...</p>
        ) : filteredDealers.length === 0 ? (
          <div className="text-center py-12"><Users className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500 font-body">No dealers found</p></div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="dealers-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-3 font-medium text-slate-600">Dealer</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Email</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Phone</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Company</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Store Count</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Docs</th>
                  <th className="px-4 py-3 font-medium text-slate-600">Portal Users</th>
                  <th className="px-4 py-3 font-medium text-slate-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDealers.map((dealer) => (
                  <tr key={dealer.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`dealer-row-${dealer.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${dealer.status === "approved" ? "bg-teal/10 text-teal" : "bg-red-100 text-red-500"}`}>
                          {dealer.first_name?.[0]}{dealer.last_name?.[0]}
                        </div>
                        <span className="font-medium text-navy">{dealer.first_name} {dealer.last_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{dealer.email}</td>
                    <td className="px-4 py-3 text-slate-600">{dealer.phone || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{dealer.company_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium" data-testid={`dealer-store-count-${dealer.id}`}>{dealer.store_count ?? 0}</td>
                    <td className="px-4 py-3"><StatusBadge status={dealer.status} testId={`dealer-status-${dealer.id}`} /></td>
                    <td className="px-4 py-3">
                      {dealer.docs_total > 0 ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${dealer.docs_uploaded >= dealer.docs_total ? "bg-green-100 text-green-700" : dealer.docs_uploaded > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid={`dealer-docs-${dealer.id}`}>
                          <FileText className="w-3 h-3" /> {dealer.docs_uploaded}/{dealer.docs_total}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-medium" data-testid={`dealer-portal-users-${dealer.id}`}>{dealer.portal_users ?? 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDetail(dealer)} className="h-8 px-2" data-testid={`manage-dealer-${dealer.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" />Manage
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Dealer Detail Dialog */}
        <Dialog open={!!selectedDealer} onOpenChange={() => setSelectedDealer(null)}>
          <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="dealer-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-navy">
                {selectedDealer ? `${selectedDealer.first_name} ${selectedDealer.last_name}` : "Dealer Details"}
              </DialogTitle>
            </DialogHeader>
            {selectedDealer && (
              <Tabs value={detailTab} onValueChange={setDetailTab}>
                <TabsList className="w-full justify-start flex-nowrap overflow-x-auto h-auto gap-1" data-testid="dealer-detail-tabs">
                  <TabsTrigger value="profile"><Edit className="w-3 h-3 mr-1" />Profile</TabsTrigger>
                  <TabsTrigger value="locations"><MapPin className="w-3 h-3 mr-1" />Locations</TabsTrigger>
                  <TabsTrigger value="notes"><MessageSquare className="w-3 h-3 mr-1" />Notes</TabsTrigger>
                  <TabsTrigger value="billing"><CreditCard className="w-3 h-3 mr-1" />Billing</TabsTrigger>
                  <TabsTrigger value="vidapay"><Zap className="w-3 h-3 mr-1" />External Systems</TabsTrigger>
                  <TabsTrigger value="documents"><FileText className="w-3 h-3 mr-1" />Documents</TabsTrigger>
                  <TabsTrigger value="employees"><UserPlus className="w-3 h-3 mr-1" />Accounts</TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile" className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>First Name</Label><Input value={profileData.first_name || ""} onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })} data-testid="dealer-first-name" /></div>
                    <div><Label>Last Name</Label><Input value={profileData.last_name || ""} onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })} data-testid="dealer-last-name" /></div>
                    <div><Label>Email</Label><Input value={profileData.email || ""} onChange={(e) => setProfileData({ ...profileData, email: e.target.value })} data-testid="dealer-email" /></div>
                    <div><Label>Phone</Label><Input value={profileData.phone || ""} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} data-testid="dealer-phone" /></div>
                    <div className="col-span-2"><Label>Company Name</Label><Input value={profileData.company_name || ""} onChange={(e) => setProfileData({ ...profileData, company_name: e.target.value })} data-testid="dealer-company" /></div>
                    <div className="col-span-2"><Label>Address</Label><Input value={profileData.address || ""} onChange={(e) => setProfileData({ ...profileData, address: e.target.value })} data-testid="dealer-address" /></div>
                    <div><Label>City</Label><Input value={profileData.city || ""} onChange={(e) => setProfileData({ ...profileData, city: e.target.value })} data-testid="dealer-city" /></div>
                    <div><Label>State</Label><Input value={profileData.state || ""} onChange={(e) => setProfileData({ ...profileData, state: e.target.value })} data-testid="dealer-state" /></div>
                    <div><Label>Zip Code</Label><Input value={profileData.zip_code || ""} onChange={(e) => setProfileData({ ...profileData, zip_code: e.target.value })} data-testid="dealer-zip" /></div>
                    <div><Label>Status</Label>
                      <Select value={selectedDealer.status || "pending"} onValueChange={async (v) => {
                        await handleStatusChange(selectedDealer.id, v);
                        if (v !== "inactive" && v !== "closed") setSelectedDealer((d) => ({ ...d, status: v }));
                      }}>
                        <SelectTrigger data-testid="dealer-profile-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleSaveProfile} disabled={saving} className="bg-teal hover:bg-teal-dark text-white" data-testid="save-dealer-profile-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit className="w-4 h-4 mr-2" />}Save Profile
                  </Button>
                  <div className="border-t pt-4 mt-4">
                    <Label className="flex items-center gap-1 mb-2"><Key className="w-4 h-4" /> Reset Password</Label>
                    <div className="flex gap-2">
                      <Input type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="dealer-new-password" />
                      <Button onClick={handleResetPassword} disabled={saving} variant="outline" data-testid="reset-password-btn">Reset</Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Locations Tab */}
                <TabsContent value="locations" className="mt-4 space-y-4">
                  <p className="text-sm text-slate-500 font-body">Assign locations to this dealer:</p>

                  {/* Assigned locations */}
                  {assignedLocationIds.length > 0 && (
                    <div className="space-y-2" data-testid="assigned-locations-list">
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Assigned ({assignedLocationIds.length})</p>
                      {allLocations.filter((l) => assignedLocationIds.includes(l.id)).map((loc) => (
                        <div key={loc.id} className="flex items-center gap-3 p-3 rounded-lg border border-teal/20 bg-teal/5">
                          <MapPin className="w-4 h-4 text-teal flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-navy font-body">{loc.name}</p>
                            <p className="text-xs text-slate-500 font-body">{loc.address}, {loc.city}, {loc.state}</p>
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 px-2" onClick={() => handleToggleLocation(loc.id)} data-testid={`remove-loc-${loc.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Search and add */}
                  <div data-testid="location-search-section">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Add Location</p>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by TSP ID, TW DC, name, or address..."
                        value={locSearch || ""}
                        onChange={(e) => setLocSearch(e.target.value)}
                        className="pl-10"
                        data-testid="location-search-input"
                      />
                    </div>
                    {locSearch && locSearch.trim().length > 0 && (
                      <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="location-search-results">
                        {allLocations
                          .filter((l) => !assignedLocationIds.includes(l.id))
                          .filter((l) => {
                            const q = locSearch.toLowerCase();
                            return l.name.toLowerCase().includes(q) ||
                              l.address?.toLowerCase().includes(q) ||
                              l.tsp_id?.toLowerCase().includes(q) ||
                              l.tw_dc?.toLowerCase().includes(q);
                          })
                          .map((loc) => (
                            <div key={loc.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" onClick={() => { handleToggleLocation(loc.id); setLocSearch(""); }} data-testid={`add-loc-${loc.id}`}>
                              <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-navy font-body">{loc.name}</p>
                                <p className="text-xs text-slate-500 font-body">{loc.address}, {loc.city}, {loc.state}</p>
                                {(loc.tsp_id || loc.tw_dc) && (
                                  <p className="text-xs text-slate-400 font-body">{loc.tsp_id ? `TSP: ${loc.tsp_id}` : ""}{loc.tsp_id && loc.tw_dc ? " · " : ""}{loc.tw_dc ? `TW DC: ${loc.tw_dc}` : ""}</p>
                                )}
                              </div>
                              <span className="text-xs text-teal font-medium flex-shrink-0">+ Add</span>
                            </div>
                          ))}
                        {allLocations
                          .filter((l) => !assignedLocationIds.includes(l.id))
                          .filter((l) => {
                            const q = locSearch.toLowerCase();
                            return l.name.toLowerCase().includes(q) ||
                              l.address?.toLowerCase().includes(q) ||
                              l.tsp_id?.toLowerCase().includes(q) ||
                              l.tw_dc?.toLowerCase().includes(q);
                          }).length === 0 && (
                          <p className="text-sm text-slate-400 p-3 text-center">No matching locations found</p>
                        )}
                      </div>
                    )}
                  </div>

                  <Button onClick={handleSaveLocations} disabled={saving} className="bg-teal hover:bg-teal-dark text-white" data-testid="save-dealer-locations-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}Save Locations
                  </Button>
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="mt-4 space-y-4">
                  <div className="flex gap-2">
                    <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note or communication log..." rows={2} className="flex-1" data-testid="dealer-note-input" />
                    <Button onClick={handleAddNote} className="bg-navy hover:bg-navy-dark text-white self-end" data-testid="add-dealer-note-btn">Add</Button>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {notes.length === 0 ? <p className="text-sm text-slate-400 font-body">No notes yet</p> : notes.map((n) => (
                      <div key={n.id} className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-700 font-body">{n.text}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          <span>{n.author}</span><Clock className="w-3 h-3" /><span>{new Date(n.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Billing Tab */}
                <TabsContent value="billing" className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Bank Name</Label><Input value={billing.bank_name || ""} onChange={(e) => setBilling({ ...billing, bank_name: e.target.value })} data-testid="billing-bank-name" /></div>
                    <div><Label>Account Holder</Label><Input value={billing.account_holder || ""} onChange={(e) => setBilling({ ...billing, account_holder: e.target.value })} data-testid="billing-account-holder" /></div>
                    <div><Label>Account Number</Label><Input value={billing.account_number || ""} onChange={(e) => setBilling({ ...billing, account_number: e.target.value })} data-testid="billing-account-number" /></div>
                    <div><Label>Routing Number</Label><Input value={billing.routing_number || ""} onChange={(e) => setBilling({ ...billing, routing_number: e.target.value })} data-testid="billing-routing-number" /></div>
                    <div><Label>Account Type</Label>
                      <Select value={billing.account_type || "checking"} onValueChange={(v) => setBilling({ ...billing, account_type: v })}>
                        <SelectTrigger data-testid="billing-account-type"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="checking">Checking</SelectItem><SelectItem value="savings">Savings</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Tax ID / EIN</Label><Input value={billing.tax_id || ""} onChange={(e) => setBilling({ ...billing, tax_id: e.target.value })} data-testid="billing-tax-id" /></div>
                    <div><Label>Payment Method</Label>
                      <Select value={billing.payment_method || "direct_deposit"} onValueChange={(v) => setBilling({ ...billing, payment_method: v })}>
                        <SelectTrigger data-testid="billing-payment-method"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="direct_deposit">Direct Deposit</SelectItem><SelectItem value="check">Check</SelectItem><SelectItem value="wire">Wire Transfer</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleSaveBilling} disabled={saving} className="bg-teal hover:bg-teal-dark text-white" data-testid="save-billing-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}Save Billing Info
                  </Button>
                </TabsContent>

                {/* External Systems Tab - Read-only from Locations */}
                <TabsContent value="vidapay" className="mt-4 space-y-6">
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-navy font-body font-semibold">External Systems</p>
                    <p className="text-xs text-slate-600 mt-1 font-body">Auto-populated from this dealer's linked locations. To edit, update the External Systems section in the Location editor.</p>
                  </div>
                  {(() => {
                    const linkedLocs = allLocations.filter((l) => assignedLocationIds.includes(l.id));
                    if (linkedLocs.length === 0) {
                      return <p className="text-sm text-slate-500 py-4 text-center">No locations linked to this dealer.</p>;
                    }
                    const locsWithVidapay = linkedLocs.filter((l) => l.vidapay_status || l.vidapay_tsp_id || l.vidapay_username);
                    const locsWithB2b = linkedLocs.filter((l) => l.b2bsoft_client_id || l.b2bsoft_access_code || l.b2bsoft_username);
                    return (
                      <>
                        {/* VidaPay */}
                        <div>
                          <div className="p-3 bg-blue-50 rounded-lg mb-3">
                            <p className="text-sm text-blue-800 font-body font-medium">VidaPay</p>
                          </div>
                          {locsWithVidapay.length === 0 ? (
                            <p className="text-sm text-slate-500 py-3 text-center">No VidaPay information configured for this dealer's locations.</p>
                          ) : (
                            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="dealer-vidapay-table">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-50 text-left">
                                    <th className="px-4 py-3 font-medium text-slate-600">Location</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Status</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">TSP ID</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Username</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Password</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {linkedLocs.map((loc) => (
                                    <tr key={loc.id} className="border-t border-slate-100" data-testid={`vidapay-row-${loc.id}`}>
                                      <td className="px-4 py-3 font-medium text-navy">{loc.name}</td>
                                      <td className="px-4 py-3">
                                        {loc.vidapay_status ? (
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${loc.vidapay_status === "active" ? "bg-emerald-100 text-emerald-700" : loc.vidapay_status === "cash_only" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                                            {loc.vidapay_status === "cash_only" ? "Cash Only" : loc.vidapay_status}
                                          </span>
                                        ) : <span className="text-slate-400">—</span>}
                                      </td>
                                      <td className="px-4 py-3 text-slate-600">{loc.vidapay_tsp_id || "—"}</td>
                                      <td className="px-4 py-3 text-slate-600">{loc.vidapay_username || "—"}</td>
                                      <td className="px-4 py-3 text-slate-600">{loc.vidapay_password ? "••••••••" : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* B2BSoft */}
                        <div>
                          <div className="p-3 bg-purple-50 rounded-lg mb-3">
                            <p className="text-sm text-purple-800 font-body font-medium">B2BSoft</p>
                          </div>
                          {locsWithB2b.length === 0 ? (
                            <p className="text-sm text-slate-500 py-3 text-center">No B2BSoft information configured for this dealer's locations.</p>
                          ) : (
                            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="dealer-b2bsoft-table">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-50 text-left">
                                    <th className="px-4 py-3 font-medium text-slate-600">Location</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Client ID</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Access Code</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Username</th>
                                    <th className="px-4 py-3 font-medium text-slate-600">Password</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {linkedLocs.map((loc) => (
                                    <tr key={loc.id} className="border-t border-slate-100" data-testid={`b2bsoft-row-${loc.id}`}>
                                      <td className="px-4 py-3 font-medium text-navy">{loc.name}</td>
                                      <td className="px-4 py-3 text-slate-600">{loc.b2bsoft_client_id || "—"}</td>
                                      <td className="px-4 py-3 text-slate-600">{loc.b2bsoft_access_code || "—"}</td>
                                      <td className="px-4 py-3 text-slate-600">{loc.b2bsoft_username || "—"}</td>
                                      <td className="px-4 py-3 text-slate-600">{loc.b2bsoft_password ? "••••••••" : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" className="mt-4 space-y-4" data-testid="admin-documents-tab">
                  {/* Text Fields Section */}
                  <DealerInfoFields dealerId={selectedDealer.id} data={preApprovedData} canEdit={can("dealers", "manage")} onSaved={setPreApprovedData} />

                  {/* Admin Upload */}
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center">
                    <input type="file" onChange={handleUploadDoc} className="hidden" id="dealer-doc-upload" data-testid="dealer-doc-upload" />
                    <label htmlFor="dealer-doc-upload" className="cursor-pointer">
                      {saving ? <Loader2 className="w-5 h-5 animate-spin text-teal mx-auto" /> : (
                        <><Upload className="w-5 h-5 text-slate-400 mx-auto mb-1" /><p className="text-xs text-slate-500">Upload additional document</p></>
                      )}
                    </label>
                  </div>

                  {/* Required Documents Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-navy mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Required Documents
                      {requiredDocs && <span className="text-xs font-normal text-slate-400">({requiredDocs.uploaded_count}/{requiredDocs.total_count})</span>}
                    </h3>
                    <div className="space-y-2">
                      {(requiredDocs?.requirements || []).map((req) => {
                        const doc = req.document;
                        return (
                          <div key={req.key} className={`p-3 rounded-lg border ${doc ? "border-slate-200 bg-white" : "border-dashed border-slate-300 bg-slate-50/50"}`} data-testid={`admin-req-doc-${req.key}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className={`w-4 h-4 flex-shrink-0 ${doc ? "text-teal" : "text-slate-300"}`} />
                                <div className="min-w-0">
                                  <span className="text-sm font-medium text-navy">{req.label}</span>
                                  {doc && (
                                    <p className="text-xs text-slate-400 truncate">{doc.original_filename} - {doc.uploaded_by} - {new Date(doc.created_at).toLocaleDateString()}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {doc && <ReviewBadge status={doc.review_status} />}
                                {doc && (
                                  <>
                                    <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer">
                                      <Button variant="ghost" size="sm" data-testid={`view-req-${req.key}`}><Eye className="w-3.5 h-3.5" /></Button>
                                    </a>
                                    <Button variant="ghost" size="sm" onClick={() => { setReviewingDoc({ ...doc, collection: "required" }); setReviewNotes(doc.review_notes || ""); }} data-testid={`review-req-${req.key}`}>
                                      <Shield className="w-3.5 h-3.5 text-amber-500" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteDoc(doc.id)} data-testid={`delete-req-${req.key}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                                {!doc && <span className="text-xs text-slate-400 italic">Not uploaded</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Location Documents Section */}
                  {(() => {
                    const paReqs = preApprovedDocs?.requirements || [];
                    const locationDocs = paReqs.filter((r) => r.section === "location");
                    const vidapayDocs = paReqs.filter((r) => r.section === "vidapay");
                    const primeDocs = paReqs.filter((r) => r.section === "prime_media");
                    return (
                      <>
                        <DocSection title="Location Documents" icon={<Camera className="w-4 h-4" />} docs={locationDocs} onDelete={handleDeletePreApprovedDoc} onReview={(doc) => { setReviewingDoc({ ...doc, collection: "pre_approved" }); setReviewNotes(doc.review_notes || ""); }} />
                        <DocSection title="VidaPay Documents" icon={<CreditCard className="w-4 h-4" />} docs={vidapayDocs} onDelete={handleDeletePreApprovedDoc} onReview={(doc) => { setReviewingDoc({ ...doc, collection: "pre_approved" }); setReviewNotes(doc.review_notes || ""); }} />
                        <DocSection title="Prime Media Documents" icon={<Building className="w-4 h-4" />} docs={primeDocs} onDelete={handleDeletePreApprovedDoc} onReview={(doc) => { setReviewingDoc({ ...doc, collection: "pre_approved" }); setReviewNotes(doc.review_notes || ""); }} />
                      </>
                    );
                  })()}

                  {/* Additional uploaded docs (general) */}
                  {documents.filter(d => !["state_id","ein_documents","articles_of_incorporation","financials","org_chart","business_plan"].includes(d.doc_type)).length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-navy mb-2">Additional Documents</h3>
                      <div className="space-y-2">
                        {documents.filter(d => !["state_id","ein_documents","articles_of_incorporation","financials","org_chart","business_plan"].includes(d.doc_type)).map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`dealer-doc-${doc.id}`}>
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <div>
                                <p className="text-sm font-medium text-navy">{doc.original_filename}</p>
                                <p className="text-xs text-slate-400">{doc.uploaded_by} - {new Date(doc.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="sm"><Download className="w-4 h-4" /></Button>
                              </a>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Review Dialog */}
                  {reviewingDoc && (
                    <Dialog open={!!reviewingDoc} onOpenChange={() => { setReviewingDoc(null); setReviewNotes(""); }}>
                      <DialogContent className="sm:max-w-md" data-testid="review-dialog">
                        <DialogHeader>
                          <DialogTitle className="text-navy">Review Document</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                          <p className="text-sm text-slate-600">{reviewingDoc.original_filename}</p>
                          <div>
                            <Label className="text-xs">Review Notes (optional)</Label>
                            <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Add notes about any issues..." className="mt-1" data-testid="review-notes" />
                          </div>
                          <div className="flex gap-2">
                            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleReviewDocument(reviewingDoc.id, "approved", reviewingDoc.collection)} data-testid="approve-doc-btn">
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => handleReviewDocument(reviewingDoc.id, "rejected", reviewingDoc.collection)} data-testid="reject-doc-btn">
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                            <Button variant="outline" onClick={() => handleReviewDocument(reviewingDoc.id, "pending", reviewingDoc.collection)} data-testid="pending-doc-btn">
                              Reset
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </TabsContent>

                {/* Employees Tab */}
                <TabsContent value="employees" className="mt-4 space-y-4">
                  {/* Owner Account Section */}
                  {ownerAccount && (
                    <Card className="border-teal/30 bg-teal/5" data-testid="owner-account-section">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-navy flex items-center gap-2">
                          <Shield className="w-4 h-4 text-teal" /> Dealer Owner Account
                          <span className="text-xs bg-teal/20 text-teal px-2 py-0.5 rounded-full font-semibold">Owner</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-500">Name</Label>
                            <p className="text-sm font-medium text-navy" data-testid="owner-name">{ownerAccount.first_name} {ownerAccount.last_name}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Status</Label>
                            <p className="text-sm font-medium text-navy capitalize" data-testid="owner-status">{ownerAccount.status}</p>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Login Email (Username)</Label>
                          <Input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} data-testid="owner-email-input" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">New Password</Label>
                          <Input type="password" placeholder="Leave blank to keep current" value={ownerPassword} onChange={(e) => setOwnerPassword(e.target.value)} data-testid="owner-password-input" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500 mb-2 block">Portal Permissions</Label>
                          <div className="grid grid-cols-3 gap-2" data-testid="owner-permissions-grid">
                            {ALL_DEALER_PERMISSIONS.map((p) => (
                              <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={ownerPerms.includes(p.key)}
                                  disabled={REQUIRED_PERMS.includes(p.key)}
                                  onChange={(e) => {
                                    if (e.target.checked) setOwnerPerms([...ownerPerms, p.key]);
                                    else setOwnerPerms(ownerPerms.filter(k => k !== p.key));
                                  }}
                                  className="rounded border-slate-300 text-teal focus:ring-teal"
                                  data-testid={`owner-perm-${p.key}`}
                                />
                                <span className={REQUIRED_PERMS.includes(p.key) ? "text-slate-400" : "text-slate-700"}>{p.label}</span>
                                {REQUIRED_PERMS.includes(p.key) && <span className="text-[10px] text-slate-400">(required)</span>}
                              </label>
                            ))}
                          </div>
                        </div>
                        <Button onClick={handleSaveOwnerAccount} disabled={ownerSaving} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-owner-account-btn">
                          {ownerSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}Save Owner Account
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  <CoOwnersSection dealerId={selectedDealer.id} stores={allLocations.filter((l) => assignedLocationIds.includes(l.id))} />

                  {/* Employees List */}
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-slate-500">{employees.length} employee{employees.length !== 1 ? "s" : ""}</p>
                    <Button onClick={() => openEmpDialog()} size="sm" className="bg-teal hover:bg-teal/90" data-testid="add-employee-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Employee
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {employees.length === 0 ? <p className="text-sm text-slate-400">No employees yet</p> : employees.map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`emp-${emp.id}`}>
                        <div>
                          <p className="text-sm font-medium text-navy">{emp.first_name} {emp.last_name}</p>
                          <p className="text-xs text-slate-500">{emp.email} | {emp.role_title || emp.role} | {Array.isArray(emp.allowed_location_ids) ? `${emp.allowed_location_ids.length} store(s)` : "All stores"}</p>
                          {emp.permissions && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {emp.permissions.slice(0, 4).map((pk) => (
                                <span key={pk} className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{pk.replace(/_/g, " ")}</span>
                              ))}
                              {emp.permissions.length > 4 && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">+{emp.permissions.length - 4}</span>}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEmpDialog(emp)} data-testid={`edit-emp-${emp.id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteEmployee(emp.id)} className="text-red-600" data-testid={`delete-emp-${emp.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Dialog open={empDialog} onOpenChange={setEmpDialog}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>{editingEmp ? "Edit" : "Add"} Employee</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>First Name *</Label><Input value={empForm.first_name} onChange={(e) => setEmpForm({ ...empForm, first_name: e.target.value })} data-testid="emp-first-name" /></div>
                          <div><Label>Last Name</Label><Input value={empForm.last_name} onChange={(e) => setEmpForm({ ...empForm, last_name: e.target.value })} data-testid="emp-last-name" /></div>
                        </div>
                        <div><Label>Email *</Label><Input value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} data-testid="emp-email" /></div>
                        <div><Label>{editingEmp ? "New Password (optional)" : "Password *"}</Label><Input type="password" value={empForm.password} onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} data-testid="emp-password" /></div>
                        <div>
                          <Label className="mb-2 block">Portal Permissions</Label>
                          <div className="grid grid-cols-2 gap-2" data-testid="emp-permissions-grid">
                            {ALL_DEALER_PERMISSIONS.map((p) => (
                              <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(empForm.permissions || []).includes(p.key)}
                                  disabled={REQUIRED_PERMS.includes(p.key)}
                                  onChange={(e) => {
                                    const current = empForm.permissions || [];
                                    if (e.target.checked) setEmpForm({ ...empForm, permissions: [...current, p.key] });
                                    else setEmpForm({ ...empForm, permissions: current.filter(k => k !== p.key) });
                                  }}
                                  className="rounded border-slate-300 text-teal focus:ring-teal"
                                  data-testid={`emp-perm-${p.key}`}
                                />
                                <span className={REQUIRED_PERMS.includes(p.key) ? "text-slate-400" : "text-slate-700"}>{p.label}</span>
                                {REQUIRED_PERMS.includes(p.key) && <span className="text-[10px] text-slate-400">(req)</span>}
                              </label>
                            ))}
                          </div>
                        </div>
                        <EmployeeStoreAccess stores={allLocations.filter((l) => assignedLocationIds.includes(l.id))}
                          value={empForm.allowed_location_ids} onChange={(v) => setEmpForm({ ...empForm, allowed_location_ids: v })} />
                        <Button onClick={handleSaveEmployee} disabled={empSaving} className="w-full bg-teal hover:bg-teal/90" data-testid="save-employee-btn">
                          {empSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Employee"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Dealer Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setCreateTab("profile");
            setCreateForm({ first_name: "", last_name: "", email: "", phone: "", company_name: "", password: "", address: "", city: "", state: "", zip_code: "" });
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-dealer-dialog">
            <DialogHeader><DialogTitle className="font-heading text-navy">Add New Dealer</DialogTitle></DialogHeader>
            <Tabs value={createTab} onValueChange={setCreateTab}>
              <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
                <TabsTrigger value="profile" data-testid="create-tab-profile"><Edit className="w-3 h-3 mr-1" />Profile</TabsTrigger>
                <TabsTrigger value="locations" data-testid="create-tab-locations"><MapPin className="w-3 h-3 mr-1" />Locations</TabsTrigger>
                <TabsTrigger value="documents" data-testid="create-tab-documents"><FileText className="w-3 h-3 mr-1" />Documents</TabsTrigger>
                <TabsTrigger value="employees" data-testid="create-tab-employees"><UserPlus className="w-3 h-3 mr-1" />Employees</TabsTrigger>
              </TabsList>

              {/* Profile Tab */}
              <TabsContent value="profile" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>First Name *</Label><Input value={createForm.first_name} onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })} data-testid="new-dealer-first-name" /></div>
                  <div><Label>Last Name</Label><Input value={createForm.last_name} onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })} data-testid="new-dealer-last-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="new-dealer-email" /></div>
                  <div><Label>Phone</Label><Input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} data-testid="new-dealer-phone" /></div>
                  <div className="col-span-2"><Label>Company Name</Label><Input value={createForm.company_name} onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })} data-testid="new-dealer-company" /></div>
                  <div className="col-span-2"><Label>Address</Label><Input value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} data-testid="new-dealer-address" /></div>
                  <div><Label>City</Label><Input value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} data-testid="new-dealer-city" /></div>
                  <div><Label>State</Label><Input value={createForm.state} onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })} data-testid="new-dealer-state" /></div>
                  <div><Label>Zip Code</Label><Input value={createForm.zip_code} onChange={(e) => setCreateForm({ ...createForm, zip_code: e.target.value })} data-testid="new-dealer-zip" /></div>
                  <div><Label>Password *</Label><Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} data-testid="new-dealer-password" /></div>
                </div>
                <Button onClick={handleCreateDealer} disabled={submitting} className="w-full bg-teal hover:bg-teal-dark text-white" data-testid="submit-create-dealer-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><UserPlus className="w-4 h-4 mr-2" />Create Dealer</>}
                </Button>
              </TabsContent>

              {/* Locked tabs - Locations, Documents, Employees */}
              {["locations", "documents", "employees"].map((tab) => (
                <TabsContent key={tab} value={tab} className="mt-4">
                  <div className="flex flex-col items-center justify-center py-12 text-center" data-testid={`create-locked-${tab}`}>
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                      {tab === "locations" && <MapPin className="w-6 h-6 text-slate-400" />}
                      {tab === "documents" && <FileText className="w-6 h-6 text-slate-400" />}
                      {tab === "employees" && <UserPlus className="w-6 h-6 text-slate-400" />}
                    </div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Save Dealer Profile First</p>
                    <p className="text-xs text-slate-400 max-w-xs">Complete and save the dealer profile on the Profile tab to unlock {tab} management.</p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={() => setCreateTab("profile")} data-testid={`create-goto-profile-from-${tab}`}>
                      <Edit className="w-3 h-3 mr-1" />Go to Profile
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <BulkUploadDealersDialog
          open={isBulkOpen}
          onOpenChange={setIsBulkOpen}
          submitting={submitting}
          onDownloadSample={downloadSampleCSV}
          onFileSelected={handleBulkUpload}
        />

        {/* Dealer status change → Reassign Stores dialog */}
        <ReassignStoresDialog
          open={!!reassignFor}
          onOpenChange={(o) => { if (!o) { setReassignFor(null); setReassignChoices({}); } }}
          dealer={reassignFor?.dealer}
          targetStatus={reassignFor?.targetStatus}
          locations={reassignFor?.locations || []}
          choices={reassignChoices}
          onChoiceChange={(locId, v) => setReassignChoices((p) => ({ ...p, [locId]: v }))}
          dealers={dealers}
          submitting={reassignSubmitting}
          onConfirm={submitReassignments}
        />
      </div>
    </AdminLayout>
  );
}
